/**
 * Match timer helpers — normalize court.startTime and manage in-progress anchor times.
 * Phase 2 will source startTime from session_courts.match_started_at on the server.
 */

import { sameCourtLineup } from './sessionPersistUtils';

/**
 * @param {Date|string|null|undefined} value
 * @returns {string|null} ISO string or null if invalid / absent
 */
export function normalizeMatchStartTime(value) {
  if (value == null) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
}

/**
 * Mark a court as in-progress and set startTime once (never overwrites an existing anchor).
 * @param {object} court - Mutated in place
 * @returns {object} The same court reference
 */
export function markCourtMatchStarted(court) {
  if (!court) return court;
  court.inProgress = true;
  if (!court.startTime) {
    court.startTime = new Date().toISOString();
  } else {
    court.startTime = normalizeMatchStartTime(court.startTime);
  }
  return court;
}

/**
 * Clear match timer state on a court.
 * @param {object} court - Mutated in place
 * @param {{ clearOriginalTeams?: boolean }} [options]
 * @returns {object} The same court reference
 */
export function clearCourtMatchTimer(court, { clearOriginalTeams = true } = {}) {
  if (!court) return court;
  court.inProgress = false;
  court.startTime = null;
  if (clearOriginalTeams) {
    court.originalTeam1 = null;
    court.originalTeam2 = null;
  }
  return court;
}

/**
 * Start a fresh match timer (new game on this court — e.g. post-score promotion).
 * @param {object} court - Mutated in place
 * @returns {object} The same court reference
 */
export function restartCourtMatchTimer(court) {
  if (!court) return court;
  court.startTime = null;
  return markCourtMatchStarted(court);
}

/**
 * Prefer local ephemeral anchor, then remote, when merging court state.
 * @param {Date|string|null|undefined} localStart
 * @param {Date|string|null|undefined} remoteStart
 * @returns {string|null}
 */
export function resolveMergedMatchStartTime(localStart, remoteStart) {
  return (
    normalizeMatchStartTime(localStart) ??
    normalizeMatchStartTime(remoteStart) ??
    null
  );
}

/**
 * Apply remote court row over local state without resetting the timer on unchanged rosters.
 * @param {object|null|undefined} prevCourt - Prior state for the same courtNumber
 * @param {object} nextCourt - Incoming remote / merged court row
 * @param {{ restartTimer?: boolean }} [options] - True when a new game started on this court
 * @returns {object}
 */
export function mergeCourtTimerFromPrevious(prevCourt, nextCourt, { restartTimer = false } = {}) {
  if (!nextCourt) return nextCourt;

  if (!nextCourt.inProgress) {
    return { ...nextCourt, startTime: null };
  }

  if (restartTimer) {
    const nextStart = normalizeMatchStartTime(nextCourt.startTime);
    return { ...nextCourt, startTime: nextStart ?? new Date().toISOString() };
  }

  const prevStart = normalizeMatchStartTime(prevCourt?.startTime);
  if (prevCourt && sameCourtLineup(prevCourt, nextCourt) && prevStart) {
    return { ...nextCourt, startTime: prevStart };
  }

  const nextStart = normalizeMatchStartTime(nextCourt.startTime);
  return { ...nextCourt, startTime: prevStart ?? nextStart ?? null };
}

/**
 * @param {number} elapsedSeconds
 * @returns {string} MM:SS
 */

export function formatElapsedMmSs(elapsedSeconds) {
  const safe = Math.max(0, Math.floor(elapsedSeconds));
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}
